import React from 'react';
import { useAuthService } from '../../auth/hooks/useAuthService';
import { AuthenticatedUser } from '../../auth/models';
import { IPermission } from '../../modules/onboarding/onboardingModels';

export interface WithAuthServiceProps {
  authService: {
    user: AuthenticatedUser | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    signIn: (options?: any) => Promise<void>;
    signOut: (options?: any) => Promise<void>;
    authFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
    hasPermission: (permission: string) => boolean;
    hasGlobalPermission: (permission: string) => boolean;
    globalPermissions: IPermission[];
    loadGlobalPermissions: () => Promise<void>;
    resetSessionTimer: () => void;
    fetchUserAllPrivileges: (scope: string) => Promise<IPermission[]>;
    checkLicensesPrivilege: (email: string, sub: string, siOrgId: string) => Promise<boolean>;
    baseURL: string;
    offering: string;
  };
}

export function withAuthService<P extends WithAuthServiceProps>(
  Component: React.ComponentType<P>
) {
  const ComponentWithAuthService = (props: Omit<P, keyof WithAuthServiceProps>) => {
    const authServiceHook = useAuthService();
    
    const authService = {
      user: authServiceHook.user,
      isAuthenticated: authServiceHook.isAuthenticated,
      isLoading: authServiceHook.isLoading,
      signIn: authServiceHook.signIn,
      signOut: authServiceHook.signOut,
      authFetch: authServiceHook.authFetch,
      hasPermission: authServiceHook.hasPermission,
      hasGlobalPermission: authServiceHook.hasGlobalPermission,
      globalPermissions: authServiceHook.globalPermissions,
      loadGlobalPermissions: authServiceHook.loadGlobalPermissions,
      fetchUserAllPrivileges: authServiceHook.fetchUserAllPrivileges,
      checkLicensesPrivilege: authServiceHook.checkLicensesPrivilege,
      resetSessionTimer: authServiceHook.resetSessionTimer,
      baseURL: authServiceHook.baseURL,
      offering: authServiceHook.offering
    };
    
    return <Component {...(props as P)} authService={authService} />;
  };
  
  ComponentWithAuthService.displayName = `withAuthService(${Component.displayName || Component.name || 'Component'})`;
  
  return ComponentWithAuthService;
}
